import { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search, Calendar, FileVideo, Pencil, X } from 'lucide-react';
import { videosAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useThumbnails } from '../hooks/useThumbnails';
import { CustomDropdown } from '../components/CustomDropdown';
import { VideoCardSkeleton, Skeleton } from '../components/Skeleton';
import toast from 'react-hot-toast';

const CATEGORIES = [
  { value: 'yoga', label: 'Yoga', icon: '🧘' },
  { value: 'dance', label: 'Dance', icon: '💃' },
  { value: 'martial_arts', label: 'Martial Arts', icon: '🥋' },
  { value: 'fitness', label: 'Fitness', icon: '💪' },
  { value: 'pilates', label: 'Pilates', icon: '🤸' },
  { value: 'meditation', label: 'Meditation', icon: '🧘' },
  { value: 'other', label: 'Other', icon: '⭐' }
];

function formatDuration(seconds) {
  if (!seconds) return 'Unknown';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default function VideosPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', category: '' });
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();
  const { studio } = useAuth();
  const queryClient = useQueryClient();

  // Fetch videos from backend - only local videos synced from desktop
  const { data: videosData, isLoading, error: videosError } = useQuery({
    queryKey: ['videos', 'local', searchQuery, filterCategory],
    queryFn: async () => {
      try {
        const result = await videosAPI.getVideos({
          source: 'local',
          search: searchQuery || undefined,
          category: filterCategory !== 'all' ? filterCategory : undefined,
          limit: 1000 // Get all videos
        });
        console.log('VideosPage - Fetched videos:', result);
        return result;
      } catch (error) {
        console.error('VideosPage - Error fetching videos:', error);
        throw error;
      }
    },
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: false,
  });

  const videos = videosData?.data?.videos || [];

  // Get thumbnails for videos (cached globally)
  const videoIds = useMemo(() => videos.map(v => v.id).filter(Boolean), [videos]);
  const thumbnailsMap = useThumbnails(videoIds);

  // Filter videos by category (client-side filtering for better UX)
  const filteredVideos = filterCategory === 'all'
    ? videos
    : videos.filter(video => video.category === filterCategory);

  const handleScheduleVideo = (videoId) => {
    // Navigate to schedule page - the schedule page will handle video selection
    navigate('/schedule', { state: { selectedVideoId: videoId } });
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setEditForm({ title: video.title || '', category: video.category || 'other' });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingVideo || !editForm.title.trim()) {
      toast.error('Title cannot be empty');
      return;
    }

    const updatedTitle = editForm.title.trim();
    const updatedCategory = editForm.category;

    // Optimistically update the cache immediately for instant feedback
    queryClient.setQueriesData(
      { predicate: (query) => query.queryKey[0] === 'videos' },
      (oldData) => {
        if (!oldData?.data?.videos) return oldData;
        return {
          ...oldData,
          data: {
            ...oldData.data,
            videos: oldData.data.videos.map(v =>
              v.id === editingVideo.id
                ? { ...v, title: updatedTitle, category: updatedCategory }
                : v
            )
          }
        };
      }
    );

    // Close modal immediately for snappy UX
    setShowEditModal(false);
    setEditingVideo(null);

    setIsSaving(true);
    try {
      await videosAPI.updateLocalVideo(editingVideo.id, {
        title: updatedTitle,
        category: updatedCategory
      });

      toast.success('Video updated successfully');
    } catch (error) {
      console.error('Error updating video:', error);
      toast.error(error.response?.data?.message || 'Failed to update video');
      // Revert on error by refetching
      queryClient.refetchQueries({ predicate: (query) => query.queryKey[0] === 'videos' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)]">Video Library</h1>
        <p className="text-sm md:text-base text-[var(--text-secondary)] mt-1 md:mt-2">View your video inventory and schedule classes</p>
        <p className="text-xs md:text-sm text-[var(--text-muted)] mt-1 hidden sm:block">
          Videos are stored locally and play on the desktop app. Use this page to view your inventory and schedule classes.
        </p>
      </div>

      {/* Search and Category Filter */}
      <div className="card" style={{ background: 'linear-gradient(135deg, var(--highlight-8), var(--highlight-5))', border: '1px solid var(--border-subtle)', overflow: 'visible', position: 'relative', zIndex: 20 }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-semibold text-[var(--text-primary)] mb-2">Search Videos</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--text-muted)] w-5 h-5" />
              <input
                type="text"
                placeholder="Search videos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-10 w-full"
                style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
              />
            </div>
          </div>

          {/* Category Filter */}
          <div>
            <CustomDropdown
              value={filterCategory}
              onChange={setFilterCategory}
              options={[
                { value: 'all', label: 'All Categories' },
                ...CATEGORIES.map(cat => ({ value: cat.value, label: `${cat.icon} ${cat.label}` }))
              ]}
              placeholder="All Categories"
              label="Filter by Category"
            />
          </div>
        </div>
        {videos.length > 0 && (
          <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--border-subtle)' }}>
            <span className="text-sm text-[var(--text-secondary)]">
              Showing {filteredVideos.length} of {videos.length} video(s)
            </span>
          </div>
        )}
      </div>

      {/* Video Grid */}
      {videosError ? (
        <div className="text-center py-12 bg-red-50 border-2 border-red-200 rounded-lg">
          <p className="text-red-800 font-semibold mb-2">Error loading videos</p>
          <p className="text-red-600 text-sm mb-2">{videosError.message || 'Failed to fetch videos from server'}</p>
          {videosError.message === 'Network Error' && (
            <div className="text-red-600 text-xs mt-2 space-y-1">
              <p>• Make sure the backend server is running on port 5000</p>
              <p>• Check that the API URL is correct: {import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}</p>
              <p>• Verify CORS is configured correctly</p>
            </div>
          )}
        </div>
      ) : isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <VideoCardSkeleton key={i} />
          ))}
        </div>
      ) : filteredVideos.length === 0 ? (
        <div className="text-center py-8 md:py-12 rounded-lg px-4" style={{ background: 'linear-gradient(135deg, var(--highlight-8), var(--highlight-5))', border: '1px solid var(--border-subtle)' }}>
          <FileVideo className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4" style={{ color: 'var(--text-muted)' }} />
          <h3 className="text-lg md:text-xl font-semibold text-[var(--text-primary)] mb-2">
            {videos.length === 0 ? 'No videos synced yet' : 'No videos match your filters'}
          </h3>
          <p className="text-sm md:text-base text-[var(--text-secondary)] mb-4">
            {videos.length === 0
              ? 'Import videos in the desktop app and they will automatically sync here.'
              : 'Try adjusting your search or category filter'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6">
          {filteredVideos.map((video) => (
            <div
              key={video.id}
              className="rounded-lg shadow-md overflow-hidden group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
            >
              {/* Video Thumbnail */}
              <div className="relative aspect-video bg-gray-200 flex items-center justify-center overflow-hidden">
                {thumbnailsMap[video.id] ? (
                  <img
                    src={thumbnailsMap[video.id]}
                    alt={video.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextElementSibling) {
                        e.target.nextElementSibling.style.display = 'flex';
                      }
                    }}
                  />
                ) : video.id && !thumbnailsMap[video.id] ? (
                  /* Show loading shimmer when thumbnail is being fetched */
                  <div className="w-full h-full relative overflow-hidden">
                    <Skeleton className="w-full h-full absolute inset-0" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-gray-300/50 animate-pulse flex items-center justify-center">
                        <FileVideo className="w-6 h-6 text-gray-400" />
                      </div>
                    </div>
                  </div>
                ) : null}
                <div className={`absolute inset-0 flex items-center justify-center ${thumbnailsMap[video.id] || video.id ? 'hidden' : ''}`}>
                  <FileVideo className="w-10 h-10 md:w-16 md:h-16 text-gray-400" />
                </div>
                <div className="absolute top-1 md:top-2 right-1 md:right-2 bg-black bg-opacity-70 text-white text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded">
                  {formatDuration(video.duration)}
                </div>
                {video.category && (
                  <div className="absolute top-1 md:top-2 left-1 md:left-2 text-[10px] md:text-xs font-semibold px-1.5 md:px-2.5 py-0.5 md:py-1 rounded-full shadow-lg flex items-center gap-0.5 md:gap-1" style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)' }}>
                    <span>{CATEGORIES.find(c => c.value === video.category)?.icon}</span>
                    <span className="hidden md:inline">{CATEGORIES.find(c => c.value === video.category)?.label}</span>
                  </div>
                )}
              </div>

              {/* Video Info */}
              <div className="p-2 md:p-4">
                <h3 className="font-semibold text-xs md:text-base text-[var(--text-primary)] mb-1 md:mb-2 line-clamp-2 group-hover:text-[var(--text-secondary)] transition-colors">
                  {video.title}
                </h3>

                {/* Actions */}
                <div className="flex gap-2 mt-2 md:mt-4">
                  <button
                    onClick={() => handleEdit(video)}
                    className="btn flex items-center justify-center gap-1 shadow-md hover:shadow-lg transition-all duration-300 text-xs md:text-sm py-1.5 md:py-2 px-2 md:px-3"
                    style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)' }}
                    title="Edit video"
                  >
                    <Pencil className="w-3 h-3 md:w-4 md:h-4" />
                    <span className="hidden md:inline">Edit</span>
                  </button>
                  <button
                    onClick={() => handleScheduleVideo(video.id)}
                    className="btn btn-primary flex-1 flex items-center justify-center gap-1 md:gap-2 shadow-md hover:shadow-lg transition-all duration-300 text-xs md:text-sm py-1.5 md:py-2"
                  >
                    <Calendar className="w-3 h-3 md:w-4 md:h-4" />
                    <span className="hidden sm:inline">Schedule</span>
                    <span className="sm:hidden">Add</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Banner */}
      <div className="rounded-lg p-3 md:p-4" style={{ background: 'var(--secondary-10)', border: '1px solid var(--secondary-20)' }}>
        <p className="text-xs md:text-sm" style={{ color: 'var(--secondary)' }}>
          <strong>Note:</strong> Videos are stored locally on your desktop app. <span className="hidden sm:inline">This page shows your video inventory so you can schedule classes. Videos will play locally when classes are viewed on the desktop app.</span>
        </p>
      </div>

      {/* Edit Video Modal */}
      {showEditModal && editingVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="w-full max-w-md rounded-lg shadow-xl" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
              <h2 className="text-lg font-semibold text-[var(--text-primary)]">Edit Video</h2>
              <button
                onClick={() => { setShowEditModal(false); setEditingVideo(null); }}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <X className="w-5 h-5 text-[var(--text-secondary)]" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4">
              {/* Video Title */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">
                  Video Title
                </label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="input w-full"
                  placeholder="Enter video title"
                  autoFocus
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">
                  Category
                </label>
                <CustomDropdown
                  value={editForm.category}
                  onChange={(value) => setEditForm({ ...editForm, category: value })}
                  options={CATEGORIES.map(cat => ({ value: cat.value, label: `${cat.icon} ${cat.label}` }))}
                  placeholder="Select category"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-4 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
              <button
                onClick={() => { setShowEditModal(false); setEditingVideo(null); }}
                className="btn px-4 py-2 text-sm"
                style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)' }}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="btn btn-primary px-4 py-2 text-sm"
                disabled={isSaving || !editForm.title.trim()}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
